import { useState, useEffect, useRef } from 'react';
import { listOrgCollection, readOrgFile, writeOrgFile } from '../../storage/FileSystemAdapter';
import { appendChangeLog } from '../../utils/changeLog';
import { levelFromScore, LEVEL_COLORS } from '../risks/RiskForm';
import PeopleSelector, { resolvePerson } from '../shared/PeopleSelector';
import EvidenceSelector from '../shared/EvidenceSelector';
import { BG_SUBTLE, TEXT_MUTED, TEXT_DISABLED, BORDER_DEFAULT, TEXT_HEADING, TEXT_DEFAULT, BG_SURFACE, BRAND_PRIMARY, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, BG_MUTED, STATUS_GOOD, BG_SHELL, STATUS_WARN, UNSAVED_AMBER, SURFACE_WHITE, FONT_PAGE_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON, FONT_STAT_VALUE } from '../../theme';

// ── STATUS COLORS ─────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  'Not Started': { bg: BG_MUTED, text: TEXT_MUTED },
  'In Progress': { bg: STATUS_WARN.bg, text: STATUS_WARN.text },
  'Completed':   { bg: STATUS_GOOD.bg, text: BRAND_PRIMARY_DARK },
};

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    title:             'Plan de tratamiento de riesgos',
    versionLabel:      'Versión',
    approvedByLabel:          'Aprobado por',
    approvalDateLabel:        'Fecha de aprobación',
    approvalEvidenceIdLabel:  'ID evidencia de aprobación',
    save:              'Guardar',
    saving:            'Guardando…',
    saveOk:            '✓ Guardado',
    saveError:         'Error al guardar',
    unsavedLabel:      'Cambios sin guardar',
    cntTotal:          'Riesgos',
    cntNotStarted:     'No iniciado',
    cntInProgress:     'En curso',
    cntCompleted:      'Completado',
    colRef:            'Ref.',
    colRisk:           'Riesgo',
    colLevel:          'Nivel',
    colTreatment:      'Opción',
    colActions:        'Acciones',
    colOwner:          'Responsable del tratamiento',
    colDeadline:       'Plazo',
    colStatus:         'Estado',
    colResidual:       'Riesgo residual',
    emptyTitle:        'No hay riesgos registrados.',
    emptyHint:         'Añade riesgos en el módulo de gestión de riesgos primero.',
    loading:           'Cargando…',
    levelHigh:         'ALTO',
    levelMedium:       'MEDIO',
    levelLow:          'BAJO',
    statusNotStarted:  'No iniciado',
    statusInProgress:  'En curso',
    statusCompleted:   'Completado',
    na:                'N/D',
  },
  en: {
    title:             'Risk Treatment Plan',
    versionLabel:      'Version',
    approvedByLabel:          'Approved by',
    approvalDateLabel:        'Approval date',
    approvalEvidenceIdLabel:  'Approval evidence ID',
    save:              'Save',
    saving:            'Saving…',
    saveOk:            '✓ Saved',
    saveError:         'Save error',
    unsavedLabel:      'Unsaved changes',
    cntTotal:          'Risks',
    cntNotStarted:     'Not Started',
    cntInProgress:     'In Progress',
    cntCompleted:      'Completed',
    colRef:            'Ref',
    colRisk:           'Risk',
    colLevel:          'Level',
    colTreatment:      'Option',
    colActions:        'Actions',
    colOwner:          'Owner',
    colDeadline:       'Deadline',
    colStatus:         'Status',
    colResidual:       'Residual risk',
    emptyTitle:        'No risks registered.',
    emptyHint:         'Add risks in the risk management module first.',
    loading:           'Loading…',
    levelHigh:         'HIGH',
    levelMedium:       'MEDIUM',
    levelLow:          'LOW',
    statusNotStarted:  'Not Started',
    statusInProgress:  'In Progress',
    statusCompleted:   'Completed',
    na:                'N/A',
  },
};

// ── HELPERS ───────────────────────────────────────────────────────────────────

function buildDefaultRtpHeader(orgId) {
  return {
    id:                  `rtp-${orgId}`,
    orgId,
    version:              '1.0',
    approvedBy:           null,
    approvalDate:         null,
    approvalEvidenceId:   null,
    updatedAt:           new Date().toISOString(),
  };
}

// Normalise treatmentActionOwner to an array. Legacy non-empty strings wrap
// into a single-element array; arrays pass through; everything else becomes [].
function ownerArray(val) {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string' && val !== '') return [val];
  return [];
}

// ── LEVEL BADGE ───────────────────────────────────────────────────────────────
function LevelBadge({ level, t }) {
  if (!level) {
    return (
      <span style={{ ...FONT_LABEL, color: TEXT_DISABLED }}>
        {t.na}
      </span>
    );
  }
  const colors = LEVEL_COLORS[level] || { bg: BG_MUTED, text: TEXT_MUTED };
  const label  = level === 'HIGH' ? t.levelHigh : level === 'LOW' ? t.levelLow : t.levelMedium;
  return (
    <span style={{
      ...FONT_SMALL,
      background: colors.bg, color: colors.text,
      borderRadius: 5, padding: '0.2rem 0.5rem',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

// ── STATUS BADGE ──────────────────────────────────────────────────────────────
function StatusBadge({ status, t }) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS['Not Started'];
  const label =
    status === 'Completed' ? t.statusCompleted :
    status === 'In Progress' ? t.statusInProgress :
    t.statusNotStarted;
  return (
    <span style={{
      ...FONT_SMALL,
      background: colors.bg, color: colors.text,
      borderRadius: 5, padding: '0.2rem 0.5rem',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      display: 'inline-block',
    }}>
      {label}
    </span>
  );
}

// ── SUMMARY CARD ──────────────────────────────────────────────────────────────
function SummaryCard({ value, label, color }) {
  return (
    <div style={{
      background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10,
      padding: '0.9rem 1.25rem',
    }}>
      <div style={{
        ...FONT_SMALL,
        color: TEXT_DEFAULT, letterSpacing: '0.04em',
        textTransform: 'uppercase', marginBottom: '0.35rem',
      }}>
        {label}
      </div>
      <div style={{
        ...FONT_STAT_VALUE, color,
      }}>
        {value}
      </div>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
export default function RTPView({ org, lang, onBack, onNavigate, onDirtyChange, saveRequestTick, onSaveResult }) {
  const t = T[lang] || T.en;

  const [risks,       setRisks]       = useState(null);   // null = loading
  const [people,      setPeople]      = useState([]);
  const [rtpData,     setRtpData]     = useState(null);   // header only
  const [rtpHeaderInitial, setRtpHeaderInitial] = useState(null);
  const [saveState,   setSaveState]   = useState('idle'); // idle | saving | ok | error
  const [dirty,       setDirty]       = useState(false);

  // ── Mount: load risks + people + rtp.json header ─────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const loadedRisks = await listOrgCollection(org.id, 'risks').catch(() => []);
      const sorted = [...loadedRisks].sort((a, b) =>
        (a.ref || a.id).localeCompare(b.ref || b.id)
      );

      const peopleData = await readOrgFile(org.id, 'people.json')
        .then(d => Array.isArray(d.people) ? d.people : [])
        .catch(() => []);

      let rtp;
      try {
        rtp = await readOrgFile(org.id, 'rtp.json');
      } catch {
        rtp = buildDefaultRtpHeader(org.id);
        try { await writeOrgFile(org.id, 'rtp.json', rtp); } catch { /* best-effort */ }
      }

      const header = {
        id:                  rtp.id || `rtp-${org.id}`,
        orgId:               rtp.orgId || org.id,
        version:             rtp.version || '1.0',
        approvedBy:          rtp.approvedBy ?? null,
        approvalDate:        rtp.approvalDate ?? null,
        approvalEvidenceId:  rtp.approvalEvidenceId ?? null,
        changeLog:           Array.isArray(rtp.changeLog) ? rtp.changeLog : [],
        updatedAt:           rtp.updatedAt || new Date().toISOString(),
      };

      if (!cancelled) {
        setRisks(sorted);
        setPeople(peopleData);
        setRtpData(header);
        setRtpHeaderInitial(header);
      }
    })();
    return () => { cancelled = true; };
  }, [org.id]);

  // ── Warn on browser refresh/close when dirty ─────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ── Propagate dirty state to App.js navigation guard ─────────────────────
  useEffect(() => {
    onDirtyChange?.(dirty);
    return () => onDirtyChange?.(false);
  }, [dirty, onDirtyChange]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleHeaderChange = (field, value) => {
    setDirty(true);
    setRtpData((prev) => {
      if (!prev) return prev;
      if (field === 'version') return { ...prev, version: value };
      return { ...prev, [field]: value || null };
    });
  };

  // ── Save (header only) ───────────────────────────────────────────────────
  const doSave = async () => {
    if (!rtpData || saveState === 'saving') return false;
    setSaveState('saving');
    try {
      const changedBy = org.consultantPersonId || 'system';

      const headerChanged = !rtpHeaderInitial || (
        rtpData.version            !== rtpHeaderInitial.version ||
        rtpData.approvedBy         !== rtpHeaderInitial.approvedBy ||
        rtpData.approvalDate       !== rtpHeaderInitial.approvalDate ||
        rtpData.approvalEvidenceId !== rtpHeaderInitial.approvalEvidenceId
      );
      let updatedHeader = rtpData;
      if (headerChanged) {
        updatedHeader = { ...rtpData, updatedAt: new Date().toISOString() };
        updatedHeader.changeLog = appendChangeLog(rtpHeaderInitial || {}, updatedHeader, changedBy);
        await writeOrgFile(org.id, 'rtp.json', updatedHeader);
      }

      setRtpHeaderInitial(updatedHeader);
      setRtpData(updatedHeader);
      setDirty(false);
      setSaveState('ok');
      setTimeout(() => setSaveState('idle'), 2000);
      return true;
    } catch {
      setSaveState('error');
      setTimeout(() => setSaveState('idle'), 3000);
      return false;
    }
  };

  const handleSave = () => { doSave(); };

  // ── App-level guard: save on tick increase, report result back ───────────
  const lastProcessedTickRef = useRef(0);
  useEffect(() => {
    if (typeof saveRequestTick !== 'number') return;
    if (saveRequestTick <= lastProcessedTickRef.current) return;
    lastProcessedTickRef.current = saveRequestTick;
    (async () => {
      const ok = await doSave();
      onSaveResult?.(ok);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (risks === null || rtpData === null) {
    return (
      <div style={{ ...FONT_LABEL, padding: '2rem', color: TEXT_MUTED }}>
        {t.loading}
      </div>
    );
  }

  // ── Computed counters (derived directly from risk records) ───────────────
  const cntTotal      = risks.length;
  let cntNotStarted = 0, cntInProgress = 0, cntCompleted = 0;
  for (const r of risks) {
    const status = r.treatmentActionStatus || 'Not Started';
    if (status === 'Completed') cntCompleted += 1;
    else if (status === 'In Progress') cntInProgress += 1;
    else cntNotStarted += 1;
  }

  // ── Styles ────────────────────────────────────────────────────────────────
  const thStyle = {
    ...FONT_TINY,
    color: TEXT_MUTED,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    padding: '0.55rem 0.75rem',
    textAlign: 'left',
    borderBottom: `1px solid ${BORDER_DEFAULT}`,
    whiteSpace: 'nowrap',
    background: BG_SUBTLE,
  };

  const tdStyle = {
    padding: '0.55rem 0.75rem',
    borderBottom: `1px solid ${BG_SHELL}`,
    verticalAlign: 'top',
  };

  const saveLabel = { idle: t.save, saving: t.saving, ok: t.saveOk, error: t.save }[saveState];
  const saveBg    = { idle: BRAND_PRIMARY, saving: TEXT_MUTED, ok: BRAND_PRIMARY_DARK, error: BRAND_PRIMARY }[saveState];
  const isSaving  = saveState === 'saving';

  const errorBannerSt = {
    ...FONT_LABEL,
    background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`,
    borderRadius: 6, padding: '0.35rem 0.75rem',
    marginBottom: '1rem',
  };

  return (
    <div>

      {/* ── Header bar ───────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem',
      }}>
        {/* Left */}
        <div>
          <h1 style={{
            ...FONT_PAGE_TITLE,
            color: TEXT_HEADING, margin: '0 0 0.6rem',
          }}>
            {t.title}
          </h1>

          {/* Inline editable header fields (formal approval artefact) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {t.versionLabel}
              </span>
              <input
                value={rtpData.version || ''}
                onChange={(e) => handleHeaderChange('version', e.target.value)}
                style={{ ...FONT_LABEL, color: TEXT_DEFAULT, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 4, padding: '0.15rem 0.4rem', background: BG_SURFACE, width: '5ch', outline: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {t.approvedByLabel}
              </span>
              <div style={{ minWidth: 200 }}>
                <PeopleSelector orgId={org.id} lang={lang} value={rtpData.approvedBy || ''} onChange={val => handleHeaderChange('approvedBy', val)} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              <span style={{ ...FONT_TINY, color: TEXT_DISABLED, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {t.approvalDateLabel}
              </span>
              <input
                type="date"
                value={rtpData.approvalDate || ''}
                onChange={(e) => handleHeaderChange('approvalDate', e.target.value)}
                style={{ ...FONT_LABEL, color: TEXT_DEFAULT, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 4, padding: '0.15rem 0.4rem', background: BG_SURFACE, outline: 'none' }}
              />
            </div>
          </div>

          {/* Approval evidence */}
          <div style={{ marginTop: '1rem' }}>
            <span style={{ display: 'block', ...FONT_TINY, color: TEXT_DISABLED, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '0.35rem' }}>
              {t.approvalEvidenceIdLabel}
            </span>
            <EvidenceSelector
              orgId={org.id}
              lang={lang}
              value={rtpData.approvalEvidenceId}
              onChange={(v) => handleHeaderChange('approvalEvidenceId', v)}
            />
          </div>

        </div>

      </div>

      {saveState === 'error' && (
        <div style={errorBannerSt}>{t.saveError}</div>
      )}

      <fieldset disabled={isSaving} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>

      {/* ── Summary bar ──────────────────────────────────────────────────── */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '0.75rem', marginBottom: '1.75rem',
      }}>
        <SummaryCard value={cntTotal}      label={t.cntTotal}      color={TEXT_HEADING} />
        <SummaryCard value={cntNotStarted} label={t.cntNotStarted} color={TEXT_MUTED} />
        <SummaryCard value={cntInProgress} label={t.cntInProgress} color={STATUS_WARN.text} />
        <SummaryCard value={cntCompleted}  label={t.cntCompleted}  color={BRAND_PRIMARY_DARK} />
      </div>

      {/* ── Empty state ───────────────────────────────────────────────────── */}
      {risks.length === 0 && (
        <div style={{
          background: BG_SURFACE, border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 10,
          padding: '3rem', textAlign: 'center',
        }}>
          <p style={{ ...FONT_BODY, color: TEXT_MUTED, margin: '0 0 0.5rem' }}>
            {t.emptyTitle}
          </p>
          <p style={{ ...FONT_LABEL, color: TEXT_DISABLED, margin: 0 }}>
            {t.emptyHint}
          </p>
        </div>
      )}

      {/* ── Risks table (read-only computed view) ────────────────────────── */}
      {risks.length > 0 && (
        <div style={{ overflowX: 'auto', borderRadius: 10, border: `1px solid ${BORDER_DEFAULT}` }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: BG_SURFACE }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: '5.5rem' }}>{t.colRef}</th>
                <th style={{ ...thStyle, minWidth: 160 }}>{t.colRisk}</th>
                <th style={{ ...thStyle, width: '5.5rem' }}>{t.colLevel}</th>
                <th style={{ ...thStyle, width: '7rem' }}>{t.colTreatment}</th>
                <th style={{ ...thStyle, minWidth: 160 }}>{t.colActions}</th>
                <th style={{ ...thStyle, minWidth: 110 }}>{t.colOwner}</th>
                <th style={{ ...thStyle, minWidth: 100 }}>{t.colDeadline}</th>
                <th style={{ ...thStyle, width: '9.5rem' }}>{t.colStatus}</th>
                <th style={{ ...thStyle, width: '6rem' }}>{t.colResidual}</th>
              </tr>
            </thead>
            <tbody>
              {risks.map((risk) => {
                const score   = risk.inherentScore || ((risk.likelihood || 0) * (risk.impact || 0));
                const level   = risk.riskLevel || (score ? levelFromScore(
                  score,
                  org.riskMatrix && org.riskMatrix.thresholds
                ) : null);
                const ownerIds = ownerArray(risk.treatmentActionOwner);
                const ownerNames = ownerIds
                  .map(id => resolvePerson(id, people))
                  .filter(Boolean)
                  .join(', ');
                const status   = risk.treatmentActionStatus || 'Not Started';

                return (
                  <tr key={risk.id}>
                    <td style={{ ...tdStyle, ...FONT_LABEL, color: TEXT_MUTED, whiteSpace: 'nowrap' }}>
                      {risk.ref || risk.id}
                    </td>
                    <td style={tdStyle}>
                      <span
                        onClick={() => onNavigate?.('risks', { viewId: risk.id })}
                        style={{
                          ...FONT_BODY_STRONG,
                          color: BRAND_PRIMARY,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        {risk.riskSummary || '—'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <LevelBadge level={level} t={t} />
                    </td>
                    <td style={{ ...tdStyle, ...FONT_LABEL, color: TEXT_MUTED, whiteSpace: 'nowrap' }}>
                      {risk.treatmentOption || '—'}
                    </td>
                    <td style={{ ...tdStyle, ...FONT_LABEL, color: TEXT_DEFAULT, whiteSpace: 'pre-wrap' }}>
                      {risk.treatmentActionProgress || '—'}
                    </td>
                    <td style={{ ...tdStyle, ...FONT_LABEL, color: TEXT_DEFAULT }}>
                      {ownerNames || '—'}
                    </td>
                    <td style={{ ...tdStyle, ...FONT_LABEL, color: TEXT_DEFAULT, whiteSpace: 'nowrap' }}>
                      {risk.treatmentDeadline || '—'}
                    </td>
                    <td style={tdStyle}>
                      <StatusBadge status={status} t={t} />
                    </td>
                    <td style={tdStyle}>
                      <LevelBadge level={risk.residualRiskLevel || null} t={t} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      </fieldset>

      {/* Sticky action bar (for header save) */}
      <div style={{
        position: 'sticky', bottom: 0,
        marginLeft: '-1.5rem', marginRight: '-1.5rem', marginBottom: '-2.5rem',
        marginTop: '1.5rem',
        padding: '0.85rem 1.5rem', background: BG_SURFACE,
        borderTop: `1px solid ${BORDER_DEFAULT}`, zIndex: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          {dirty && (
            <span style={{ ...FONT_LABEL, color: UNSAVED_AMBER }}>
              {t.unsavedLabel}
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saveState === 'saving'}
            style={{
              ...FONT_BUTTON,
              background: saveBg, color: SURFACE_WHITE, border: 'none',
              borderRadius: 8, padding: '0.55rem 1.25rem',
              cursor: saveState === 'saving' ? 'default' : 'pointer',
              transition: 'background 0.2s', whiteSpace: 'nowrap',
            }}
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
